import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import styles from '../styles.module.css';
import classNames from 'classnames';
import WhiteboardController from '../controllers/WhiteboardController';
import { type CursorPosition } from '../types';

interface CursorComponentProps {
  cursor: CursorPosition;
}
const CursorComponent: React.FC<CursorComponentProps> = ({ cursor }) => {
  const backgroundColor = cursor.color;
  const controller = useController(WhiteboardController);
  const { panZoomState } = controller.state;

  // Force a re-render when the pan or zoom changes.
  const a = panZoomState; // eslint-disable-line @typescript-eslint/no-unused-vars

  // Translate the cursor position from canvas to window coordinates.
  const windowCoords = controller.canvas.rectToWindowCoords({
    left: cursor.x!,
    top: cursor.y!,
    width: 0,
    height: 0,
  });

  return (
    <div
      className={styles.cursor}
      style={{ left: windowCoords.left, top: windowCoords.top }}
      onMouseOver={event => {
        controller.actionShowCursorName(cursor.cursorId);
        controller.canvas?.fire('cursor:mousemove', event);
      }}
      onMouseUp={event => {
        controller.canvas?.fire('cursor:mouseup', event);
      }}
      onMouseDown={event => {
        controller.canvas?.fire('cursor:mousedown', event);
      }}
    >
      <svg
        className={styles.cursorPointer}
        width='23'
        height='25'
        viewBox='0 0 23 25'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <g filter='url(#filter0_d_56_7381)'>
          <path
            d='M4.93311 3L17.8586 10.8275L11.7943 11.7942L8.89466 17.5824L4.93311 3Z'
            fill={backgroundColor}
          />
          <path
            d='M4.93311 3L17.8586 10.8275L11.7943 11.7942L8.89466 17.5824L4.93311 3Z'
            stroke='white'
          />
        </g>
        <defs>
          <filter
            id='filter0_d_56_7381'
            x='0.433105'
            y='0.5'
            width='21.9255'
            height='23.5825'
            filterUnits='userSpaceOnUse'
          >
            <feFlood floodOpacity='0' result='BackgroundImageFix' />
            <feColorMatrix
              in='SourceAlpha'
              type='matrix'
              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
              result='hardAlpha'
            />
            <feOffset dy='2' />
            <feGaussianBlur stdDeviation='2' />
            <feColorMatrix
              type='matrix'
              values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0'
            />
            <feBlend
              mode='normal'
              in2='BackgroundImageFix'
              result='effect1_dropShadow_56_7381'
            />
            <feBlend
              mode='normal'
              in='SourceGraphic'
              in2='effect1_dropShadow_56_7381'
              result='shape'
            />
          </filter>
        </defs>
      </svg>

      <div
        className={classNames(styles.cursorName, {
          [styles.cursorHideName]: cursor.hideName && !cursor.forceShowName,
        })}
        style={{ backgroundColor }}
      >
        <aha-avatar
          class={styles.cursorAvatar}
          src={cursor.userInfo.avatar}
          size={20}
          borderless
          name={cursor.userInfo.isLoaded ? cursor.userInfo.displayName : ''}
        />
        {cursor.userInfo.displayName}
      </div>
    </div>
  );
};

const Cursor = ApplicationView(CursorComponent);

const CursorLayer = () => {
  const controller = useController(WhiteboardController);
  const { cursors } = controller.state;

  return (
    <div className={styles.cursors}>
      {Object.values(cursors).map(cursor => (
        <Cursor key={cursor.cursorId} cursor={cursor} />
      ))}
    </div>
  );
};

export default ApplicationView(CursorLayer);
